import type {
  CompilerTemplateFile,
  CompilerTemplateResponse,
  CreateSessionResponse,
  CrdtFilesResponse,
  CrdtHistoryResponse,
  CrdtHistorySnapshot,
  CrdtHistorySnapshotPage,
  CrdtInfo,
  IncomingMessage,
} from "./types"

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null
}

function asString(value: unknown): string | null {
  return typeof value === "string" ? value : null
}

export function parseTemplateResponse(payload: unknown): CompilerTemplateResponse | null {
  if (!isRecord(payload)) return null

  const id = asString(payload.id)
  const name = asString(payload.name)
  const mainFile = asString(payload.main_file)
  const filesRaw = Array.isArray(payload.files) ? payload.files : null

  if (!id || !name || !mainFile || !filesRaw) return null

  const files: CompilerTemplateFile[] = []

  for (const entry of filesRaw) {
    if (!isRecord(entry)) continue

    const path = asString(entry.path ?? entry.name)
    const content = asString(entry.content) ?? ""
    if (!path) continue

    files.push({
      path,
      content,
      is_asset: Boolean(entry.is_asset),
    })
  }

  return {
    id,
    name,
    description: asString(payload.description) ?? undefined,
    main_file: mainFile,
    files,
  }
}

function parseCrdtInfo(value: unknown): CrdtInfo | null {
  if (!isRecord(value)) return null
  const room = asString(value.room)
  const ws_url = asString(value.ws_url)
  const reconstruct_url = asString(value.reconstruct_url)
  const history_url = asString(value.history_url)
  if (!room || !ws_url || !reconstruct_url || !history_url) return null
  return {
    provider: asString(value.provider) ?? undefined,
    room,
    ws_url,
    reconstruct_url,
    history_url,
  }
}

export function parseCrdtFilesResponse(payload: unknown): CrdtFilesResponse | null {
  if (!isRecord(payload)) return null
  const sessionId = asString(payload.session_id)
  const filesRaw = payload.files
  if (!sessionId || !isRecord(filesRaw)) return null

  const files: Record<string, string> = {}
  for (const [path, content] of Object.entries(filesRaw)) {
    if (typeof content === "string") files[path] = content
  }

  return { session_id: sessionId, files }
}

export function parseCrdtHistoryResponse(payload: unknown): CrdtHistoryResponse | null {
  if (!isRecord(payload)) return null
  const sessionId = asString(payload.session_id)
  const latestVersion =
    typeof payload.latest_version === "number" && Number.isFinite(payload.latest_version)
      ? payload.latest_version
      : null
  const deltasRaw = Array.isArray(payload.deltas) ? payload.deltas : null
  if (!sessionId || latestVersion === null || !deltasRaw) return null

  const deltas = []
  for (const row of deltasRaw) {
    if (!isRecord(row)) continue
    const version =
      typeof row.version === "number" && Number.isFinite(row.version) ? row.version : null
    const pathRaw = row.path
    const path =
      pathRaw === null || pathRaw === undefined
        ? null
        : asString(pathRaw)?.trim() || null
    const update = asString(row.update)
    const at = asString(row.at)
    const deltaSessionId = asString(row.session_id) ?? sessionId
    if (version === null || update === null || !at) continue
    deltas.push({
      version,
      session_id: deltaSessionId,
      path,
      update,
      at,
    })
  }

  return {
    session_id: sessionId,
    latest_version: latestVersion,
    deltas,
  }
}

function parseSnapshotRow(row: unknown, fallbackSessionId: string, fallbackProjectId: string): CrdtHistorySnapshot | null {
  if (!isRecord(row)) return null
  const version = typeof row.version === "number" && Number.isFinite(row.version) ? row.version : null
  const state = asString(row.state)
  const at = asString(row.at)
  if (version === null || !state || !at) return null
  return {
    version,
    session_id: asString(row.session_id) ?? fallbackSessionId,
    project_id: asString(row.project_id) ?? fallbackProjectId,
    user_id: asString(row.user_id) ?? "",
    user_name: asString(row.user_name) ?? "",
    state,
    at,
    date: asString(row.date) ?? at.slice(0, 10),
  }
}

/** Unwrap `{ success, data: { groups: [{ date, snapshots }] } }`. */
export function parseCrdtHistorySnapshotPage(payload: unknown): CrdtHistorySnapshotPage | null {
  if (!isRecord(payload)) return null
  const data = isRecord(payload.data) ? payload.data : payload
  if (!isRecord(data)) return null
  const sessionId = asString(data.session_id)
  const projectId = asString(data.project_id) ?? ""
  const groups = Array.isArray(data.groups) ? data.groups : null
  if (!sessionId || !groups) return null

  const snapshots: CrdtHistorySnapshot[] = []
  for (const group of groups) {
    if (!isRecord(group)) continue
    const groupDate = asString(group.date) ?? ""
    const rows = Array.isArray(group.snapshots) ? group.snapshots : []
    for (const row of rows) {
      const parsed = parseSnapshotRow(row, sessionId, projectId)
      if (!parsed) continue
      if (!parsed.date && groupDate) parsed.date = groupDate
      snapshots.push(parsed)
    }
  }

  const totalCount = typeof data.total_count === "number" ? data.total_count : snapshots.length
  const page = typeof data.page === "number" ? data.page : 1
  const pageSize = typeof data.page_size === "number" ? data.page_size : snapshots.length
  const totalPages = typeof data.total_pages === "number" ? data.total_pages : 1

  return {
    project_id: projectId,
    session_id: sessionId,
    snapshots,
    total_count: totalCount,
    page,
    page_size: pageSize,
    total_pages: totalPages,
  }
}

export function parseCreateSessionResponse(payload: unknown): CreateSessionResponse | null {
  if (!isRecord(payload)) return null

  const sessionId = asString(payload.session_id)
  const createdAt = asString(payload.created_at)

  if (!sessionId || !createdAt) return null

  return {
    session_id: sessionId,
    ws_url: asString(payload.ws_url) ?? undefined,
    auto_compile: Boolean(payload.auto_compile),
    created_at: createdAt,
    message: asString(payload.message) ?? undefined,
    pdf_url: asString(payload.pdf_url) ?? undefined,
    crdt: parseCrdtInfo(payload.crdt) ?? undefined,
  }
}

export function parseIncomingWsMessage(payload: unknown): IncomingMessage | null {
  if (!isRecord(payload)) return null
  const type = asString(payload.type)
  if (!type) return null

  return {
    type,
    data: isRecord(payload.data) ? payload.data : undefined,
  }
}
